import React from 'react';
import { Plus, MessageSquare, Loader2 } from 'lucide-react';
import { ConversationListItem } from '../atoms/ConversationListItem';
import type { Conversation } from '../../../types/assistant';

interface ConversationSidebarProps {
  conversations: Conversation[];
  activeId: string | null;
  isLoading: boolean;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onPin: (id: string, isPinned: boolean) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  activeId,
  isLoading,
  onSelect,
  onNewChat,
  onPin,
  onDelete,
  onRename,
}) => {
  return (
    <div
      className="flex flex-col h-full bg-[#0d0d12] border-r border-white/5"
      data-test-id="conversation-sidebar"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-indigo-400" />
          <h2 className="text-sm font-semibold text-white">Conversations</h2>
        </div>
        <button
          onClick={onNewChat}
          className="p-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors shadow-lg shadow-indigo-600/20"
          title="New chat"
          data-test-id="new-chat-button"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1 custom-scrollbar">
        {isLoading && (
          <div className="flex items-center justify-center py-8" data-test-id="conversations-loading">
            <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
          </div>
        )}

        {!isLoading && conversations.length === 0 && (
          <div className="text-center py-8 px-4" data-test-id="conversations-empty">
            <MessageSquare className="h-8 w-8 text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No conversations yet</p>
            <p className="text-xs text-gray-600 mt-1">Start a new chat to begin</p>
          </div>
        )}

        {conversations.map(conv => (
          <ConversationListItem
            key={conv.id}
            id={conv.id}
            title={conv.title}
            lastMessageAt={conv.last_message_at}
            isPinned={conv.is_pinned}
            isActive={conv.id === activeId}
            onClick={() => onSelect(conv.id)}
            onPin={() => onPin(conv.id, conv.is_pinned)}
            onDelete={() => onDelete(conv.id)}
            onRename={(newTitle) => onRename(conv.id, newTitle)}
          />
        ))}
      </div>
    </div>
  );
};
